"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Plus, ExternalLink, Clock, Loader2 } from "lucide-react";
import { WorkspaceFile } from "@/lib/workspace-context";
import { cn } from "@/lib/utils";

interface HoverPreviewData {
  linkName: string;
  rect: DOMRect;
}

interface WikiLinkPreviewProps {
  preview: HoverPreviewData | null;
  files: WorkspaceFile[];
  onOpen: (fileId: string) => void;
  onCreate: (linkName: string) => void;
  onClose: () => void;
}

export function WikiLinkPreview({
  preview,
  files,
  onOpen,
  onCreate,
  onClose,
}: WikiLinkPreviewProps) {
  const [localPreview, setLocalPreview] = useState<HoverPreviewData | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preview) {
      setLocalPreview(preview);
      setLoading(true);
      setContent(null);
      const cleanName = preview.linkName.replace(".md", "").toLowerCase().trim();
      const found = files.find(
        (f) =>
          f.type === "file" &&
          f.name.replace(".md", "").toLowerCase().trim() === cleanName
      );
      const timer = setTimeout(() => {
        setContent(found?.content || null);
        setLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setLocalPreview(null);
      setContent(null);
      setLoading(false);
    }
  }, [preview, files]);

  if (!localPreview) return null;

  const linkName = localPreview.linkName;
  const cleanName = linkName.replace(".md", "").toLowerCase().trim();
  const found = files.find(
    (f) =>
      f.type === "file" &&
      f.name.replace(".md", "").toLowerCase().trim() === cleanName
  );
  const rect = localPreview.rect;

  const previewText = content
    ? content
        .replace(/#{1,6}\s+/g, "")
        .replace(/\*\*/g, "")
        .replace(/\*/g, "")
        .replace(/`{1,3}[\s\S]*?`{1,3}/g, "")
        .replace(/\[\[([^\]]+)\]\]/g, "$1")
        .slice(0, 200) + (content.length > 200 ? "\u2026" : "")
    : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 6, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 4, scale: 0.96 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        onMouseLeave={onClose}
        className="fixed z-[150] w-72 p-4 rounded-xl bg-card border border-border/60 shadow-xl shadow-black/10 liquid-glass pointer-events-auto"
        style={{
          top: Math.min(rect.bottom + 8, window.innerHeight - 230),
          left: Math.max(8, Math.min(rect.left, window.innerWidth - 304)),
        }}
      >
        {found ? (
          <>
            <div className="flex items-center gap-2 mb-2.5">
              <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">
                  {found.name.replace(".md", "")}
                </p>
                {found.project && (
                  <p className="text-[10px] text-muted-foreground">{found.project}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="ml-auto p-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-secondary/60 transition-colors"
              >
                <span className="text-[10px]">\u00d7</span>
              </button>
            </div>

            {loading ? (
              <div className="py-3 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : previewText ? (
              <p className="text-[11px] text-muted-foreground leading-snug line-clamp-4 mb-3 text-pretty">
                {previewText}
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground/60 italic mb-3">
                Sin contenido
              </p>
            )}

            <div className="flex items-center gap-2 pt-2.5 border-t border-border/30">
              {found.lastModified && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60 flex-1">
                  <Clock className="w-3 h-3" />
                  {found.lastModified}
                </span>
              )}
              <button
                onClick={() => onOpen(found.id)}
                className="flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground rounded-md text-[10px] font-medium hover:bg-primary/90 active:scale-[0.98] transition-all"
              >
                <ExternalLink className="w-3 h-3" />
                Abrir
              </button>
            </div>
          </>
        ) : (
          <div className="text-center">
            <p className="text-xs font-medium text-foreground mb-1">
              Nota no encontrada
            </p>
            <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
              Esta nota no existe todav\u00eda.
            </p>
            <button
              onClick={() => onCreate(linkName)}
              className="flex items-center justify-center gap-1 w-full h-8 px-3 bg-primary text-primary-foreground rounded-md text-[11px] font-medium hover:bg-primary/90 active:scale-[0.98] transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              Crear nota
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}